import { ChevronDown, ChevronRight, type LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

interface MockTokens {
	background: string;
	surface: string;
	surfaceSecondary: string;
	foreground: string;
	muted: string;
	default: string;
	default300: string;
	border: string;
	separator: string;
	success: string;
	warning: string;
	danger: string;
	surfaceShadow: string;
	chartDesktop: string;
	chartMobile: string;
	chartGrid: string;
	dotRamp: ReadonlyArray<string>;
}

export const MOCK_TOKENS: MockTokens = {
	background: "oklch(0.9702 0 0)",
	surface: "#ffffff",
	surfaceSecondary: "oklch(0.9524 0.0013 286.37)",
	foreground: "oklch(0.2103 0.0059 285.89)",
	muted: "oklch(0.5517 0.0138 285.94)",
	default: "oklch(94% 0.001 286.375)",
	default300: "oklch(0.87 0.002 286.3)",
	border: "oklch(90% 0.004 286.32)",
	separator: "oklch(92% 0.004 286.32)",
	success: "oklch(0.7329 0.1935 150.81)",
	warning: "oklch(0.7819 0.1585 72.33)",
	danger: "oklch(0.6532 0.2328 25.74)",
	surfaceShadow: "0 2px 4px 0 rgba(0,0,0,0.04), 0 1px 2px 0 rgba(0,0,0,0.06), 0 0 1px 0 rgba(0,0,0,0.06)",
	chartDesktop: "#3b82f6",
	chartMobile: "#a855f7",
	chartGrid: "#e5e7eb",
	dotRamp: ["#22c55e", "#ef4444", "#a855f7", "#f59e0b", "#facc15", "#3b82f6", "#ec4899"],
};

export type MockChipColor = "default" | "accent" | "success" | "warning" | "danger";

function softStyle(color: string): CSSProperties {
	return {
		background: `color-mix(in oklab, ${color} 15%, transparent)`,
		color: `color-mix(in oklab, ${color} 70%, ${MOCK_TOKENS.foreground} 30%)`,
	};
}

interface MockChipProps {
	children: ReactNode;
	color?: MockChipColor;
	size?: "xs" | "sm";
	icon?: LucideIcon;
	className?: string;
}

export function MockChip({ children, color = "default", size = "sm", icon: Icon, className = "" }: MockChipProps) {
	const { palette } = useCommunityVariant();
	const style: CSSProperties =
		color === "default"
			? { background: MOCK_TOKENS.default, color: MOCK_TOKENS.foreground }
			: color === "accent"
				? { background: palette.accentSoft, color: palette.accentInk }
				: softStyle(MOCK_TOKENS[color]);
	const sizeClass = size === "xs" ? "px-1.5 text-[10px] leading-4" : "px-2 py-0.5 text-[11px] leading-4";
	return (
		<span className={`inline-flex shrink-0 items-center gap-1 rounded-2xl font-medium ${sizeClass} ${className}`} style={style}>
			{Icon ? <Icon className="size-3" /> : null}
			{children}
		</span>
	);
}

type MockButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";

interface MockButtonProps {
	children?: ReactNode;
	variant?: MockButtonVariant;
	size?: "xs" | "sm";
	icon?: LucideIcon;
	fullWidth?: boolean;
	onClick?: () => void;
	pressed?: boolean;
	ariaLabel?: string;
	className?: string;
	iconOnly?: boolean;
}

export function MockButton({
	children,
	variant = "primary",
	size = "sm",
	icon: Icon,
	fullWidth,
	onClick,
	pressed,
	ariaLabel,
	className = "",
	iconOnly,
}: MockButtonProps) {
	const { palette } = useCommunityVariant();
	const style: CSSProperties =
		variant === "primary"
			? { background: palette.accent, color: "#fff" }
			: variant === "secondary"
				? { background: MOCK_TOKENS.default, color: MOCK_TOKENS.foreground }
				: variant === "outline"
					? { border: `1px solid ${MOCK_TOKENS.border}`, color: MOCK_TOKENS.foreground, background: "transparent" }
					: variant === "danger"
						? { background: MOCK_TOKENS.danger, color: "#fff" }
						: { color: MOCK_TOKENS.foreground, background: "transparent" };
	const sizeClass = size === "xs" ? "h-6 text-[10px]" : "h-7 text-[11px]";
	const padClass = iconOnly ? (size === "xs" ? "w-6" : "w-7") : size === "xs" ? "px-2" : "px-3";
	const base = `inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl font-medium whitespace-nowrap ${sizeClass} ${padClass} ${
		fullWidth ? "w-full" : ""
	} ${className}`;
	const content = (
		<>
			{Icon ? <Icon className={size === "xs" ? "size-3" : "size-3.5"} /> : null}
			{children}
		</>
	);
	if (onClick) {
		return (
			<button
				type="button"
				onClick={onClick}
				aria-pressed={pressed}
				aria-label={ariaLabel}
				className={`${base} transition hover:brightness-95`}
				style={style}
			>
				{content}
			</button>
		);
	}
	return (
		<span className={base} style={style} aria-hidden={iconOnly ? true : undefined} title={ariaLabel}>
			{content}
		</span>
	);
}

interface MockCardProps {
	children: ReactNode;
	className?: string;
	padded?: boolean;
	dataMock?: string;
}

export function MockCard({ children, className = "", padded = true, dataMock }: MockCardProps) {
	return (
		<div
			data-mock={dataMock}
			className={`rounded-xl bg-white ${padded ? "p-3" : ""} ${className}`}
			style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
		>
			{children}
		</div>
	);
}
interface PresenceDotProps {
	online: boolean;
	size?: number;
	className?: string;
}

export function PresenceDot({ online, size = 10, className = "" }: PresenceDotProps) {
	return (
		<span
			aria-hidden="true"
			className={`absolute bottom-0 right-0 rounded-full border-2 border-white ${className}`}
			style={{ width: size, height: size, background: online ? MOCK_TOKENS.success : MOCK_TOKENS.default300 }}
		/>
	);
}

interface UnreadBadgeProps {
	count: number;
	tone?: "accent" | "danger";
	className?: string;
	floating?: boolean;
}

export function UnreadBadge({ count, tone = "danger", className = "", floating }: UnreadBadgeProps) {
	const { palette } = useCommunityVariant();
	const label = count > 99 ? "99+" : String(count);
	return (
		<span
			aria-hidden="true"
			className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white ${
				floating ? "absolute -right-1.5 -top-1.5" : ""
			} ${className}`}
			style={{ background: tone === "accent" ? palette.accent : MOCK_TOKENS.danger }}
		>
			{label}
		</span>
	);
}
interface MockFieldProps {
	placeholder: string;
	icon?: LucideIcon;
	value?: string;
	suffix?: string;
	className?: string;
	label?: string;
}

export function MockField({ placeholder, icon: Icon, value, suffix, className = "", label }: MockFieldProps) {
	return (
		<div className={`flex flex-col gap-1 ${className}`}>
			{label ? <span className="text-[11px] font-medium text-neutral-800">{label}</span> : null}
			<div
				className="flex h-8 items-center gap-2 rounded-xl px-3 text-[11px]"
				style={{
					background: MOCK_TOKENS.surfaceSecondary,
					boxShadow: MOCK_TOKENS.surfaceShadow,
					color: value ? MOCK_TOKENS.foreground : MOCK_TOKENS.muted,
				}}
			>
				{Icon ? <Icon className="size-3.5 shrink-0" style={{ color: MOCK_TOKENS.muted }} /> : null}
				<span className="min-w-0 flex-1 truncate">{value ?? placeholder}</span>
				{suffix ? (
					<span className="shrink-0" style={{ color: MOCK_TOKENS.muted }}>
						{suffix}
					</span>
				) : null}
			</div>
		</div>
	);
}

interface MockSelectProps {
	value: string;
	className?: string;
}

export function MockSelect({ value, className = "" }: MockSelectProps) {
	return (
		<span
			className={`inline-flex h-7 shrink-0 items-center gap-1.5 rounded-xl px-2.5 text-[11px] font-medium ${className}`}
			style={{ background: MOCK_TOKENS.surfaceSecondary, boxShadow: MOCK_TOKENS.surfaceShadow, color: MOCK_TOKENS.foreground }}
		>
			<span className="truncate">{value}</span>
			<ChevronDown className="size-3 shrink-0" style={{ color: MOCK_TOKENS.muted }} />
		</span>
	);
}

interface MockTabsProps {
	items: ReadonlyArray<string>;
	active: number;
	onChange?: (index: number) => void;
	ariaLabel?: string;
	className?: string;
}

export function MockTabs({ items, active, onChange, ariaLabel, className = "" }: MockTabsProps) {
	const wrapperClass = `inline-flex items-center gap-0.5 rounded-xl p-0.5 ${className}`;
	const wrapperStyle = { background: MOCK_TOKENS.default };
	const renderItem = (item: string, index: number) => {
		const isActive = index === active;
		const classes = `rounded-lg px-2.5 py-1 text-[11px] font-medium ${isActive ? "bg-white text-neutral-900" : "text-neutral-500"}`;
		const style = isActive ? { boxShadow: MOCK_TOKENS.surfaceShadow } : undefined;
		return onChange ? (
			<button
				key={item}
				type="button"
				role="tab"
				aria-selected={isActive}
				onClick={() => onChange(index)}
				className={classes}
				style={style}
			>
				{item}
			</button>
		) : (
			<span key={item} className={classes} style={style}>
				{item}
			</span>
		);
	};
	if (onChange) {
		return (
			<div className={wrapperClass} style={wrapperStyle} role="tablist" aria-label={ariaLabel}>
				{items.map(renderItem)}
			</div>
		);
	}
	return (
		<div className={wrapperClass} style={wrapperStyle} aria-hidden="true">
			{items.map(renderItem)}
		</div>
	);
}

export function MockBreadcrumbs({ items }: { items: ReadonlyArray<string> }) {
	return (
		<div className="flex items-center gap-1 text-[11px]" aria-hidden="true">
			{items.map((item, index) => {
				const isLast = index === items.length - 1;
				return (
					<span key={item} className="inline-flex items-center gap-1">
						<span className={isLast ? "font-medium text-neutral-900" : "text-neutral-500"}>{item}</span>
						{isLast ? null : <ChevronRight className="size-3 text-neutral-400" />}
					</span>
				);
			})}
		</div>
	);
}
